<script setup>
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  hint: { type: String, required: true },
  funnelLabel: { type: String, required: true },
  funnelPlaceholder: { type: String, required: true },
  modeLabel: { type: String, required: true },
  modeAddLabel: { type: String, required: true },
  modeReplaceLabel: { type: String, required: true },
  loadingItemsLabel: { type: String, required: true },
  emptyItemsLabel: { type: String, required: true },
  cancelLabel: { type: String, required: true },
  confirmLabel: { type: String, required: true },
  funnelId: { type: String, required: true },
  funnelOptions: { type: Array, required: true },
  mode: { type: String, required: true },
  loadingItems: { type: Boolean, default: false },
  items: { type: Array, required: true },
  selectedItemIds: { type: Array, required: true },
  submitting: { type: Boolean, default: false },
  itemRowParts: { type: Function, required: true },
});

const emit = defineEmits([
  'update:funnelId',
  'update:mode',
  'toggleItem',
  'confirm',
  'cancel',
]);

function handleToggleItem(itemId) {
  emit('toggleItem', itemId);
}
</script>

<template>
  <div class="overflow-y-auto flex-1 flex flex-col gap-4 px-4 pb-2 min-h-0">
    <div class="rounded-2xl border border-n-weak bg-n-surface-1">
      <div class="px-3.5 pt-3.5 pb-2 border-b border-n-weak/80">
        <div class="flex gap-2 items-start">
          <span
            class="mt-0.5 i-ri-layout-grid-line size-4 text-n-slate-10 shrink-0"
            aria-hidden="true"
          />
          <p class="mb-0 text-xs leading-snug text-n-slate-11">
            {{ hint }}
          </p>
        </div>
      </div>
      <div class="p-3.5">
        <div class="flex flex-col gap-1.5 min-w-0">
          <div class="flex gap-1.5 items-center text-xs font-medium text-n-slate-11">
            <span
              class="i-ri-route-line size-3.5 text-n-teal-11 shrink-0"
              aria-hidden="true"
            />
            {{ funnelLabel }}
          </div>
          <div
            class="w-full min-w-0 [&>div]:!w-full [&_select]:w-full [&_select]:min-w-0"
          >
            <Select
              :model-value="funnelId"
              :options="funnelOptions"
              :placeholder="funnelPlaceholder"
              @update:model-value="value => emit('update:funnelId', value)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-2 px-0.5">
      <span class="text-xs font-medium text-n-slate-11">{{ modeLabel }}</span>
      <label class="flex gap-2 items-center text-sm cursor-pointer text-n-slate-12">
        <input
          :checked="mode === 'add'"
          type="radio"
          value="add"
          class="rounded-full border-n-weak text-n-brand focus:ring-n-brand"
          @change="emit('update:mode', 'add')"
        />
        {{ modeAddLabel }}
      </label>
      <label class="flex gap-2 items-center text-sm cursor-pointer text-n-slate-12">
        <input
          :checked="mode === 'replace'"
          type="radio"
          value="replace"
          class="rounded-full border-n-weak text-n-brand focus:ring-n-brand"
          @change="emit('update:mode', 'replace')"
        />
        {{ modeReplaceLabel }}
      </label>
    </div>
    <div v-if="loadingItems" class="text-sm text-n-slate-11">
      {{ loadingItemsLabel }}
    </div>
    <ul
      v-else
      class="overflow-y-auto flex-1 min-h-[10rem] max-h-[min(50vh,320px)] p-0 m-0 list-none rounded-xl border divide-y divide-n-weak border-n-weak"
    >
      <li v-if="items.length === 0" class="px-3 py-4 text-sm text-center text-n-slate-11">
        {{ emptyItemsLabel }}
      </li>
      <li v-for="item in items" :key="item.id">
        <label
          class="flex gap-3 items-center px-3 py-2.5 transition-colors cursor-pointer hover:bg-n-alpha-2"
        >
          <input
            type="checkbox"
            class="rounded border-n-weak shrink-0 text-n-brand focus:ring-n-brand"
            :checked="selectedItemIds.includes(item.id)"
            @change="handleToggleItem(item.id)"
          />
          <template v-for="row in [itemRowParts(item)]" :key="`assign-row-${item.id}`">
            <span class="flex flex-1 gap-2 items-center min-w-0">
              <span class="text-sm truncate text-n-slate-12">{{ row.primaryText }}</span>
              <span
                v-if="row.stage"
                class="inline-flex flex-shrink-0 items-center px-2 py-0.5 text-xxs font-medium rounded-full border leading-none max-w-[10rem] truncate"
                :style="{
                  backgroundColor: `${row.stage.color}20`,
                  color: row.stage.color,
                  borderColor: `${row.stage.color}40`,
                }"
              >
                {{ row.stage.name }}
              </span>
            </span>
          </template>
        </label>
      </li>
    </ul>
  </div>
  <div
    class="flex flex-shrink-0 gap-2 justify-end items-center px-4 py-3 border-t border-n-weak bg-n-background"
  >
    <Button
      type="button"
      variant="faded"
      color="slate"
      size="sm"
      :label="cancelLabel"
      :aria-label="cancelLabel"
      :disabled="submitting"
      @click="emit('cancel')"
    />
    <Button
      type="button"
      color="blue"
      size="sm"
      :label="confirmLabel"
      :is-loading="submitting"
      :disabled="selectedItemIds.length === 0 || submitting || loadingItems"
      @click="emit('confirm')"
    />
  </div>
</template>
